import { Container } from 'pixi.js';
import type { LayoutMetrics } from '../LayoutManager';

/**
 * Abstract base class for all game scenes
 * Provides lifecycle management and common functionality
 */
export abstract class BaseScene {
  public readonly container: Container;
  protected isActive = false;

  constructor() {
    this.container = new Container();
    this.container.visible = false;
  }

  /**
   * Called when the scene becomes active
   * Override to set up scene-specific state and animations
   */
  onEnter(): void {
    this.isActive = true;
    this.container.visible = true;
  }

  /**
   * Called every frame while the scene is active
   * Override for per-frame updates
   */
  onUpdate(_delta: number): void {
    // Override in subclasses
  }

  /**
   * Called when the scene is about to become inactive
   * Override to clean up animations and temporary state
   */
  onExit(): void {
    this.isActive = false;
    this.container.visible = false;
  }

  /**
   * Called when the scene is being destroyed
   * Override to clean up all resources
   */
  dispose(): void {
    this.container.destroy({ children: true });
  }

  /**
   * Called when the screen is resized
   * Override to update layout with new metrics
   */
  onResize(_metrics: LayoutMetrics): void {
    // Override in subclasses
  }

  /**
   * Build the scene's visual content
   * Called during construction and can be called to rebuild
   */
  protected abstract build(): void;
}
